import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	LinearProgress,
} from "@mui/material";
import * as yup from "yup";
import { makeStyles } from "tss-react/mui";
import AddDialogStyle from "styles/application/AddDialogStyle";
import {
	currentUTCDateTime,
	generateErrorState,
	handleSort,
	handleValidateObj,
	isChrome,
	defaultPageSize,
} from "helpers/utils";
import { useDispatch } from "react-redux";
import DyanamicDropdown from "components/Elements/DynamicDropdown";
import { getPublishedModel } from "services/models/modelList";
import ErrorInputFieldWrapper from "components/Layouts/ErrorInputFieldWrapper";
import {
	getModelIntervals,
	getModelVersionArrangements,
} from "services/models/modelDetails/modelIntervals";
import { getModelRolesListByInterval } from "services/models/modelDetails/modelRoles";
import { showError } from "../../../redux/common/actions";
import { getModelAvailableAsset } from "services/models/modelDetails/modelAsset";
import TextFieldContainer from "components/Elements/TextFieldContainer";
import { Grid } from "@mui/material";
import AddModel from "pages/Models/ModelDetails/ModelAsset/AddModel";
import DeleteIcon from "assets/icons/deleteIcon.svg";
import { getModelDeparments } from "services/models/modelDetails/details";
import { FULL_ACCESS } from "constants/AccessTypes/AccessTypes";
import SiteAssetRegisterDropdown from "components/Elements/SiteAssetRegisterDropdown";

// Init styled components
const ADD = AddDialogStyle();

// Yup validation schema
const schema = (
	modelTemplateType,
	siteAssetRegisterID,
	siteAssetName
) =>
	yup.object({
		workOrder: yup
			.string("This field must be a string")
			.required("This field is required"),
		note: yup.string("This field must be a string").nullable(),
		siteAssetDescription: yup.string("This field must be a string").nullable(),
		siteAssetReferenceName: yup.string("This field must be a string").nullable(),
		siteAssetPlannerGroup: yup.string("This field must be a string").nullable(),
		siteAssetWorkCenter: yup.string("This field must be a string").nullable(),
		modelID: yup
			.string("This field is required")
			.required("The field is required"),
		modelVersionIntervalId: yup
			.string("This field is required")
			.required("The field is required"),

		modelVersionRoleId: yup
			.string("This field is required")
			.required("The field is required"),
		siteAssetRegisterID: yup
			.string("This field is required")
			.nullable(),
		siteAssetName: yup
			.string("This field is required")
			.nullable(),
		siteDepartmentID: yup
			.string("This field is required")
			.required("The field is required"),
		scheduledDate: yup
			.string("This field is required")
			.required("The field is required"),
		notificationNumber: yup.string("This field must be a string").nullable(),
		clientName: yup.string("This field must be a string").nullable(),
		modelVersionArrangementID: yup.string("Arragement is required").nullable(),
	});

const useStyles = makeStyles()((theme) => ({
	dialogContent: {
		width: 500,
	},
	assetButton: {
		width: "100%",
		marginTop: "30px",
	},
	deleteIcon: {
		cursor: "pointer",
		marginTop: 40,
	},
}));

// Default state schemas
const defaultErrorSchema = {
	workOrder: null,
	note: null,
	modelID: null,
	modelVersionRoleId: null,
	siteAssetRegisterID: null,
	scheduledDate: null,
	notificationNumber: null,
	modelVersionIntervalId: null,
	modelVersionArrangementID: null,
	siteDepartmentID: null,
	siteAssetName: null,
	siteAssetReferenceName: null,
	siteAssetPlannerGroup: null,
	siteAssetWorkCenter: null,
	clientName: null,
};
const defaultStateSchema = {
	workOrder: "",
	note: "",
	notificationNumber: "",
	modelID: {},
	modelVersionRoleId: {},
	modelVersionIntervalId: {},
	siteAssetRegisterID: {},
	siteAssetName: "",
	siteAssetReferenceName: "",
	siteAssetPlannerGroup: "",
	siteAssetWorkCenter: "",
	siteDepartmentID: {},
	scheduledDate: currentUTCDateTime(),
	siteAssetDescription: "",
	clientName: "",
	modelVersionArrangementID: ""
};

function AddNewServiceDetail({
	open,
	closeHandler,
	siteAppId,
	title,
	createProcessHandler,
	customCaptions,
	setSearchQuery,
	fetchData,
	setDataForFetchingService,
	showArrangements,
	position,
	allowRegisterAssetsForServices,
	showServiceClientName,
	showAssetReferences,
}) {
	// Init hooks
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	// Init state
	const [isUpdating, setIsUpdating] = useState(false);
	const [input, setInput] = useState(defaultStateSchema);
	const [errors, setErrors] = useState(defaultErrorSchema);
	const [intervals, setIntervals] = useState([]);
	const [roles, setRoles] = useState([]);
	const [modelAssets, setModelAssets] = useState([]);
	const [modelFocus, setModelFocus] = useState(true);
	const [openAsset, setOpenAsset] = useState(false);
	const [arrangementsData, setArrangementDatas] = useState(undefined);
	const [departments, setDepartments] = useState([]);

	const [dataSourceAfterModelChange, setDataSourceAfterModelChange] = useState(
		[]
	);

	const modelId = input?.modelID?.activeModelVersionID;
	const intervalId = input?.modelVersionIntervalId?.id;
	const modelAssestID = input?.modelID?.id;

	const arrangeNameFrom = (arrangementId) => {
		if (!arrangementId) return "";
		const arrangement = arrangementsData?.find(arr => arr.id === arrangementId);
		return arrangement ? arrangement.name : "";
	};

	// attempting to fetch interval when model is selected
	useEffect(() => {
		if (modelId) {
			const fetchInterval = async () => {
				const response = await getModelIntervals(modelId);
				if (response.status) {
					setIntervals(response.data);

					// automatically select role if role list contains single data
					if (response.data.length === 1) {
						setInput((prev) => ({
							...prev,
							modelVersionIntervalId: response?.data?.[0],
						}));
					}
				}
			};
			fetchInterval();
		}
	}, [modelId]);

	useEffect(() => {
		if (modelAssestID) {
			const fetchAssetData = async () => {
				const response = await getModelAvailableAsset(modelAssestID);
				if (response.status) {
					setModelAssets(response.data);
				} else {
					dispatch(showError(response?.data || t("message.couldNotFetchData")));
				}
			};
			fetchAssetData();
		}
	}, [modelAssestID]);

	useEffect(() => {
		if (showArrangements && modelId) {
			const getModelArrangements = async () => {
				const response = await getModelVersionArrangements(modelId);
				if (response.status) {
					setArrangementDatas(response?.data);
					// automatically select arrangement if arrangement list contains single data
					if (response.data.length === 1) {
						setInput((prev) => ({
							...prev,
							modelVersionArrangementID: response?.data?.[0]?.id,
						}));
					}
				} else {
					dispatch(showError(response?.data || t("message.couldNotFetchData")));
				}
			};
			getModelArrangements();
		}
	}, [showArrangements, modelId, dispatch]);

	// attempting to fetch role when interval is selected
	useEffect(() => {
		if (intervalId) {
			const fetchInterval = async () => {
				const response = await getModelRolesListByInterval(intervalId);
				if (response.status) {
					setRoles(response?.data);

					// automatically select role if role list contains single data
					if (response.data.length === 1) {
						const data = response?.data?.[0];
						setInput((prev) => ({
							...prev,
							modelVersionRoleId: data,
							siteDepartmentID: data?.siteDepartmentID
								? {
										name: data?.siteDepartmentName,
										id: data?.siteDepartmentID,
								  }
								: {},
						}));
					}
				}
			};
			fetchInterval();
		}
	}, [intervalId]);

	//for fetching departments list
	useEffect(() => {
		if (
			input?.modelVersionRoleId?.id &&
			!input?.modelVersionRoleId?.siteDepartmentID
		) {
			const fetchModelDepartments = async () => {
				const response = await getModelDeparments(modelId);
				if (response.status) {
					const modifiedResponse = response.data
						.filter((d) => d.id)
						.map((data) => ({
							...data,
							id: data?.modelDepartmentID,
						}));
					setDepartments(modifiedResponse);
					if (modifiedResponse?.length === 1) {
						setInput((prev) => ({
							...prev,
							siteDepartmentID: modifiedResponse?.[0],
						}));
					}
				} else {
					dispatch(showError(response?.data || t("message.couldNotFetchData")));
				}
			};
			fetchModelDepartments();
		}
	}, [
		input?.modelVersionRoleId?.id,
		dispatch,
		modelId,
		input?.modelVersionRoleId?.siteDepartmentID,
	]);

	const closeOverride = () => {
		// Clearing input state and errors
		setInput(defaultStateSchema);
		setErrors(defaultErrorSchema);
		closeHandler();
	};

	const handleOpenAssetModal = () => {
		setOpenAsset(true);
	};

	const handleCloseAsset = () => {
		setOpenAsset(false);
	};

	const handleAddAsset = (value) => {
		setInput((prev) => {
			const newState = {
				...prev,
				siteAssetName: value?.name || value?.asset?.name,
				siteAssetReferenceName: value?.assetReferenceName || value?.asset?.assetReferenceName,
				siteAssetDescription: value?.description,
				siteAssetPlannerGroup: value?.plannerGroup,
				siteAssetWorkCenter: value?.workCenter,
				modelVersionArrangementID: value?.modelVersionArrangementID?.id || value?.modelVersionArrangementID
			};
			return newState;
		});
		setOpenAsset(false);
	};

	const handleCreateProcess = async () => {
		// clear search data
		setSearchQuery("");

		// Rendering spinner
		setIsUpdating(true);

		// Clearing errors before attempted create
		setErrors(defaultErrorSchema);

		const selectedSiteAssetRegisterId =
			input?.siteAssetRegisterID?.id || input?.siteAssetRegisterID?.siteAssetID || null;

		// cleaned Input
		const cleanInput = {
			...input,
			modelID: input?.modelID?.id,
			modelVersionIntervalId: input?.modelVersionIntervalId?.id,
			modelVersionRoleId: input?.modelVersionRoleId?.id,
			siteAssetRegisterID: selectedSiteAssetRegisterId,
			siteAssetName: input?.siteAssetName || undefined,
			siteAssetReferenceName: input?.siteAssetReferenceName || undefined,
			siteAssetDescription: input?.siteAssetDescription || undefined,
			siteAssetPlannerGroup: input?.siteAssetPlannerGroup || undefined,
			siteAssetWorkCenter: input?.siteAssetWorkCenter || undefined,
			siteDepartmentID: input?.siteDepartmentID?.id,
			note: input?.note || null,
			notificationNumber: input?.notificationNumber || null,
			clientName: input?.clientName || null,
			modelVersionArrangementID: input?.modelVersionArrangementID?.id || input?.modelVersionArrangementID || null,
			scheduledDate: input?.scheduledDate
				? new Date(new Date(input?.scheduledDate).getTime()).toJSON()
				: "",
		};

		try {
			const localChecker = await handleValidateObj(
				schema(
					input?.modelID?.modelTemplateType,
					selectedSiteAssetRegisterId,
					input?.siteAssetName
				),
				cleanInput
			);

			const requiresAssetSelection =
				input?.modelID?.modelTemplateType === "A" &&
				!selectedSiteAssetRegisterId &&
				!input?.siteAssetName;
			
				// Attempting API call if no local validaton errors
			if (!localChecker.some((el) => el.valid === false) && !requiresAssetSelection) {
				const { siteAssetReferenceName, ...restCleanInput } = cleanInput;
				const payload = {
					...restCleanInput,
					siteAssetRegisterID: selectedSiteAssetRegisterId,
					siteAssetReference: siteAssetReferenceName,
					siteAppId: siteAppId,
				};

				const newData = await createProcessHandler(payload);
				if (newData.status === true) {
					setDataForFetchingService({
						pageNumber: 1,
						pageSize: defaultPageSize(),
						search: "",
						sortField: "",
						sort: "",
					});
					await fetchData();
					setIsUpdating(false);
					closeOverride();
				} else {
					setIsUpdating(false);
					dispatch(
						showError(newData?.data?.detail || t("message.failedToAddService"))
					);
				}
			} else {
				// show validation errors
				const newErrors = generateErrorState(localChecker);
				if (requiresAssetSelection) {
					newErrors.siteAssetRegisterID = [t("validation.required")];
				}
				setErrors({ ...errors, ...newErrors });
				setIsUpdating(false);
			}
		} catch (err) {
			// TODO: handle non validation errors here
			setIsUpdating(false);
			setErrors({ ...errors, ...err?.response?.data?.errors });
			dispatch(showError(t("message.failedToAddService")));
		}
	};

	const handleRoleChange = (val) => {
		setInput({ ...input, modelVersionRoleId: val });
		if (val?.siteDepartmentID) {
			setInput((prev) => ({
				...prev,
				siteDepartmentID: {
					name: val?.siteDepartmentName,
					id: val?.siteDepartmentID,
				},
			}));
		} else {
			setInput((prev) => ({
				...prev,
				siteDepartmentID: {},
			}));
		}
	};

	const handleClearAsset = () => {
		setInput((prev) => ({
			...prev,
			siteAssetName: "",
			siteAssetReferenceName: "",
			siteAssetPlannerGroup: "",
			siteAssetWorkCenter: "",
			siteAssetRegisterID: {},
		}));
	};

	const departmentReadOnly =
		!input?.modelVersionRoleId?.id ||
		input?.modelVersionRoleId?.siteDepartmentID ||
		departments.length === 1;

	return (
		<div>
			<AddModel
				open={openAsset}
				handleClose={handleCloseAsset}
				modelId={modelId}
				title={customCaptions?.asset}
				handleServiceAddComplete={handleAddAsset}
				serviceAccess={position?.serviceAccess}
				isFromService={true}
				arrangementDatas={arrangementsData}
				arrangementTitle={customCaptions?.arrangement}
				hideArrangements={
					!showArrangements ||
					!input.modelID.hasArrangements ||
					!input.modelID.modelTemplateType === FULL_ACCESS ||
					arrangementsData?.length === 0
				}
				showAssetReferences={showAssetReferences}
				customCaptions={customCaptions}
			/>
			<Dialog
				open={open}
				onClose={closeOverride}
				aria-labelledby="alert-dialog-title"
				aria-describedby="alert-dialog-description"
				className="large-application-dailog"
				disableEnforceFocus={isChrome() ? modelFocus : false}
			>
				{isUpdating ? <LinearProgress /> : null}

				<ADD.ActionContainer>
					<DialogTitle id="alert-dialog-title">
						{<ADD.HeaderText>{title}</ADD.HeaderText>}
					</DialogTitle>
					<ADD.ButtonContainer>
						<div className="modalButton">
							<ADD.CancelButton
								onClick={closeOverride}
								variant="contained"
								onFocus={(e) => {
									setModelFocus(true);
								}}
							>
								{t("action.cancel")}
							</ADD.CancelButton>
						</div>
						<div className="modalButton">
							<ADD.ConfirmButton
								onClick={handleCreateProcess}
								variant="contained"
								className={classes.createButton}
								disabled={isUpdating}
							>
								{title}
							</ADD.ConfirmButton>
						</div>
					</ADD.ButtonContainer>
				</ADD.ActionContainer>

				<DialogContent className={classes.dialogContent}>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ADD.NameLabel>
								{customCaptions?.serviceWorkOrder ?? "Work Order"}
								<ADD.RequiredStar>*</ADD.RequiredStar>
							</ADD.NameLabel>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.workOrder === null ? null : errors.workOrder
								}
							>
								<ADD.NameInput
									error={errors.workOrder === null ? false : true}
									value={input.workOrder}
									onChange={(e) => {
										setInput({ ...input, workOrder: e.target.value });
									}}
									variant="outlined"
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>

						<ADD.RightInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={errors.modelID === null ? null : errors.modelID}
							>
								<DyanamicDropdown
									isServerSide={false}
									width="100%"
									placeholder={t("placeholder.selectEntity", { entity: customCaptions.model })}
									dataHeader={[
										{ id: 1, name: t("label.name") },
										{ id: 2, name: customCaptions.model },
									]}
									columns={[
										{ id: 1, name: "name" },
										{ id: 2, name: "modelName" },
									]}
									showHeader
									selectedValue={{
										...input["modelID"],
										name: !input?.modelID?.name
											? ""
											: !input?.modelID?.modelName
											? input?.modelID?.name
											: input?.modelID?.name + " " + input?.modelID?.modelName,
									}}
									handleSort={handleSort}
									onChange={(val) => {
										setInput({
											...input,
											modelID: val,
											modelVersionIntervalId: {},
											modelVersionRoleId: {},
											siteAssetRegisterID: {},
											siteDepartmentID: {},
										});
										setDataSourceAfterModelChange([]);
									}}
									selectdValueToshow={"name"}
									label={customCaptions?.model}
									required
									isError={errors.modelID === null ? false : true}
									fetchData={() => getPublishedModel(siteAppId)}
								/>
							</ErrorInputFieldWrapper>
						</ADD.RightInputContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.modelVersionIntervalId === null
										? null
										: errors.modelVersionIntervalId
								}
							>
								<DyanamicDropdown
									dataSource={intervals}
									isServerSide={false}
									width="100%"
									placeholder={t("placeholder.selectEntity", { entity: customCaptions.interval })}
									dataHeader={[{ id: 1, name: customCaptions.interval }]}
									columns={[{ id: 1, name: "name" }]}
									selectedValue={input["modelVersionIntervalId"]}
									handleSort={handleSort}
									onChange={(val) => {
										setInput({
											...input,
											modelVersionIntervalId: val,
											modelVersionRoleId: {},
											siteDepartmentID: {},
										});
									}}
									selectdValueToshow="name"
									label={customCaptions?.interval}
									required
									isError={
										errors.modelVersionIntervalId === null ? false : true
									}
									isReadOnly={
										input?.modelID?.activeModelVersionID === null ||
										input?.modelID?.activeModelVersionID === undefined
									}
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>

						{input?.modelID?.modelTemplateType === "A" ? (
							<ADD.RightInputContainer>
								<ErrorInputFieldWrapper
									errorMessage={
										errors.siteAssetRegisterID === null ? null : errors.siteAssetRegisterID
									}
								>
									{position?.assetAccess === FULL_ACCESS &&
									allowRegisterAssetsForServices ? (
										input?.siteAssetName ? (
											<Grid container spacing={2}>
												<Grid item xs={11} md={11}>
													<SiteAssetRegisterDropdown
														dataSource={modelAssets}
														selectedValue={{
															assetNumber: input.siteAssetName,
															reference: input.siteAssetReferenceName,
															description: input.siteAssetDescription,
														}}
														disabled={true}
														isReadOnly={true}
														customCaptions={customCaptions}
														showReferenceColumn={showAssetReferences}
														showSelectedReference={false}
														isError={errors.siteAssetRegisterID === null ? false : true}
														required={true}
														label={customCaptions?.asset}
														isServerSide={false}
													/>
												</Grid>
												<Grid item xs={1} md={1}>
													<img
														className={classes.deleteIcon}
														src={DeleteIcon}
														alt="Clear"
														onClick={handleClearAsset}
													/>
												</Grid>
											</Grid>
										) : (
											<Grid container spacing={2}>
												<Grid item xs={9} md={9}>
													<SiteAssetRegisterDropdown
														dataSource={modelAssets}
														selectedValue={input.siteAssetRegisterID?.id ? input.siteAssetRegisterID : null}
														onChange={(val) => {
															setInput({
																...input,
																siteAssetRegisterID: val,
															});
														}}
														onClear={() =>
															setInput((prev) => ({
																...prev,
																siteAssetRegisterID: {},
															}))
														}
														customCaptions={customCaptions}
														showReferenceColumn={showAssetReferences}
														showSelectedReference={false}
														isError={errors.siteAssetRegisterID === null ? false : true}
														required={true}
														disabled={
															input?.modelID?.id === null ||
															input?.modelID?.id === undefined
														}
														isServerSide={false}
														label={customCaptions?.asset}
													/>
												</Grid>
												<Grid item xs={3} md={3}>
													<ADD.ConfirmButton
														onClick={handleOpenAssetModal}
														variant="contained"
														className={classes.assetButton}
														disabled={input?.siteAssetRegisterID?.id || input?.siteAssetRegisterID?.siteAssetID}
													>
														{t("action.addEntity", { entity: customCaptions?.asset })}
													</ADD.ConfirmButton>
												</Grid>
											</Grid>
										)
									) : (
										<SiteAssetRegisterDropdown
											dataSource={modelAssets}
											selectedValue={input.siteAssetRegisterID?.id ? input.siteAssetRegisterID : null}
											onChange={(val) => {
												setInput({ ...input, siteAssetRegisterID: val });
											}}
											onClear={() =>
												setInput((prev) => ({
													...prev,
													siteAssetRegisterID: {},
												}))
											}
											customCaptions={customCaptions}
											showReferenceColumn={showAssetReferences}
											showSelectedReference={false}
											isError={errors.siteAssetRegisterID === null ? false : true}
											required={true}
											disabled={
												input?.modelID?.id === null ||
												input?.modelID?.id === undefined
											}
											isServerSide={false}
											label={customCaptions?.asset}
										/>
									)}
								</ErrorInputFieldWrapper>
							</ADD.RightInputContainer>
						) : (
							<ADD.RightInputContainer>
								<ErrorInputFieldWrapper
									errorMessage={
										errors.modelVersionRoleId === null
											? null
											: errors.modelVersionRoleId
									}
								>
									<DyanamicDropdown
										dataSource={roles}
										isServerSide={false}
										width="100%"
										placeholder={t("placeholder.selectEntity", { entity: customCaptions.role })}
										dataHeader={[{ id: 1, name: customCaptions.role }]}
										columns={[{ id: 1, name: "name" }]}
										selectedValue={input["modelVersionRoleId"]}
										handleSort={handleSort}
										onChange={handleRoleChange}
										selectdValueToshow="name"
										label={customCaptions?.role}
										required
										isError={errors.modelVersionRoleId === null ? false : true}
										isReadOnly={
											input?.modelVersionIntervalId?.id === null ||
											input?.modelVersionIntervalId?.id === undefined
										}
									/>
								</ErrorInputFieldWrapper>
							</ADD.RightInputContainer>
						)}
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.LeftInputContainer>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.scheduledDate === null ? null : errors.scheduledDate
								}
							>
								<TextFieldContainer
									label={t("label.scheduledDate")}
									name={"scheduledDate"}
									value={input.scheduledDate}
									onChange={(e) => {
										setInput({ ...input, scheduledDate: e.target.value });
									}}
									isRequired={true}
									type="datetime-local"
									placeholder={t("placeholder.selectDate")}
									error={errors.scheduledDate === null ? false : true}
								/>
							</ErrorInputFieldWrapper>
						</ADD.LeftInputContainer>
						{input?.modelID?.modelTemplateType === "A" && (
							<ADD.RightInputContainer>
								<ErrorInputFieldWrapper
									errorMessage={
										errors.modelVersionRoleId === null
											? null
											: errors.modelVersionRoleId
									}
								>
									<DyanamicDropdown
										dataSource={roles}
										isServerSide={false}
										width="100%"
										placeholder={t("placeholder.selectEntity", { entity: customCaptions.role })}
										dataHeader={[{ id: 1, name: customCaptions.role }]}
										columns={[{ id: 1, name: "name" }]}
										selectedValue={input["modelVersionRoleId"]}
										handleSort={handleSort}
										onChange={handleRoleChange}
										selectdValueToshow="name"
										label={customCaptions?.role}
										required
										isError={errors.modelVersionRoleId === null ? false : true}
										isReadOnly={
											input?.modelVersionIntervalId?.id === null ||
											input?.modelVersionIntervalId?.id === undefined
										}
									/>
								</ErrorInputFieldWrapper>
							</ADD.RightInputContainer>
						)}

						{input?.modelID?.modelTemplateType !== "A" && (
							<ADD.RightInputContainer>
								<ErrorInputFieldWrapper
									errorMessage={
										errors.siteDepartmentID === null
											? null
											: errors.siteDepartmentID
									}
								>
									<DyanamicDropdown
										dataSource={departments}
										isServerSide={false}
										width="100%"
										placeholder={t("placeholder.selectEntity", { entity: customCaptions.department })}
										dataHeader={[{ id: 1, name: customCaptions.department }]}
										columns={[{ id: 1, name: "name" }]}
										selectedValue={input["siteDepartmentID"]}
										handleSort={handleSort}
										onChange={(val) => {
											setInput({ ...input, siteDepartmentID: val });
										}}
										selectdValueToshow="name"
										label={customCaptions?.department}
										required
										isError={errors.modelVersionRoleId === null ? false : true}
										isReadOnly={departmentReadOnly}
									/>
								</ErrorInputFieldWrapper>
							</ADD.RightInputContainer>
						)}
					</ADD.InputContainer>
					<ADD.InputContainer>
						{showServiceClientName && (
							<ADD.LeftInputContainer>
								<ErrorInputFieldWrapper
									errorMessage={
										errors.notificationNumber === null
											? null
											: errors.notificationNumber
									}
								>
									<TextFieldContainer
										label={t("label.clientName")}
										name={"clientName"}
										value={input?.clientName}
										onChange={(e) =>
											setInput({
												...input,
												clientName: e.target.value,
											})
										}
										isRequired={false}
									/>
								</ErrorInputFieldWrapper>
							</ADD.LeftInputContainer>
						)}
						{input?.modelID?.modelTemplateType === "A" && (
							<ADD.RightInputContainer
								style={{
									paddingLeft: `${showServiceClientName ? "15px" : "0px"}`,
									paddingRight: `${showServiceClientName ? "0px" : "14px"}`,
								}}
							>
								<ErrorInputFieldWrapper
									errorMessage={
										errors.siteDepartmentID === null
											? null
											: errors.siteDepartmentID
									}
								>
									<DyanamicDropdown
										dataSource={departments}
										isServerSide={false}
										width="100%"
										placeholder={t("placeholder.selectEntity", { entity: customCaptions.department })}
										dataHeader={[{ id: 1, name: customCaptions.department }]}
										columns={[{ id: 1, name: "name" }]}
										selectedValue={input["siteDepartmentID"]}
										handleSort={handleSort}
										onChange={(val) => {
											setInput({ ...input, siteDepartmentID: val });
										}}
										selectdValueToshow="name"
										label={customCaptions?.department}
										required
										isError={errors.modelVersionRoleId === null ? false : true}
										isReadOnly={departmentReadOnly}
									/>
								</ErrorInputFieldWrapper>
							</ADD.RightInputContainer>
						)}
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.FullWidthContainer style={{ paddingRight: 0 }}>
							<ErrorInputFieldWrapper
								errorMessage={
									errors.notificationNumber === null
										? null
										: errors.notificationNumber
								}
							>
								<TextFieldContainer
									label={customCaptions?.notificationNumber}
									name={"notificationNumber"}
									value={input?.notificationNumber}
									onChange={(e) =>
										setInput({ ...input, notificationNumber: e.target.value })
									}
									isRequired={false}
								/>
							</ErrorInputFieldWrapper>
						</ADD.FullWidthContainer>
					</ADD.InputContainer>
					<ADD.InputContainer>
						<ADD.FullWidthContainer style={{ paddingRight: 0 }}>
							<ADD.NameLabel>{t("label.notes")}</ADD.NameLabel>

							<ADD.NameInput
								value={input.note}
								onChange={(e) => {
									setInput({ ...input, note: e.target.value });
								}}
								variant="outlined"
								multiline
								onBlur={() => {
									setModelFocus(false);
								}}
							/>
						</ADD.FullWidthContainer>
					</ADD.InputContainer>
				</DialogContent>
			</Dialog>
		</div>
	);
}

export default AddNewServiceDetail;
